import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { KeyRound, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const rules = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "A number", test: (p) => /\d/.test(p) },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const passed = rules.filter((r) => r.test(next));
  const allPass = passed.length === rules.length;
  const matches = next.length > 0 && next === confirm;
  const canSubmit = token.length > 0 && allPass && matches && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/auth/reset/confirm", { token, new_password: next });
      toast.success("Password set. Signing you in…");
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A18] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <div className="rounded-2xl border border-slate-800 bg-[#0A1124] p-8">
          <div className="flex items-center gap-2 text-white mb-1">
            <KeyRound className="h-5 w-5 text-cyan-300" />
            <h1 className="font-display text-2xl font-semibold">Set a new password</h1>
          </div>
          <p className="text-sm text-slate-400 mb-6">
            Choose a strong password. This link can only be used once.
          </p>

          {!token ? (
            <p className="text-sm text-rose-400" data-testid="reset-no-token">
              This reset link is missing its token. Please request a new one.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="reset-new" className="text-slate-300">New password</Label>
                <Input
                  id="reset-new"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  data-testid="reset-new-password-input"
                  className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                />
                <ul className="mt-2 grid grid-cols-2 gap-1.5">
                  {rules.map((r) => {
                    const ok = r.test(next);
                    return (
                      <li key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-400" : "text-slate-500"}`}>
                        {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                        {r.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-confirm" className="text-slate-300">Confirm new password</Label>
                <Input
                  id="reset-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password-input"
                  className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                />
                {confirm.length > 0 && !matches && (
                  <p className="text-xs text-rose-400">Passwords do not match.</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="reset-submit"
                className="w-full h-11 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set Password & Sign In"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
